/** The record fact band (DESIGN.md "Person record" → Account band; catalog plan §2 "Fact
 *  band"): the spine's band styling without the nodes — page background, a bottom hairline,
 *  one column per cell (min 132 px; title `body2` 600, a datum line, a muted detail line,
 *  tabular numerals), scrolling sideways below its minimum width; not sticky. Each line stays
 *  on one line and ends in an ellipsis (a wrapped line was clipped mid-word at 390 px); a line
 *  the ellipsis cut shows its full text in a tooltip on hover (`BandLine`; the accessible name
 *  is the whole text anyway).
 *
 *  Facts, not big numbers: each cell leads to what owns its facts — a link (a page tab) or a
 *  button (a panel or card of the record) — and is plain text when the viewer cannot reach
 *  the owner. The record's band decides where each cell leads (`targetOf`); this draws it.
 *  Shared by the user's account band and the catalog records' bands.
 *
 *  `FactBandSkeleton` is the band while its record loads: the same chrome, grid and lines, so
 *  the loaded band lands where it stood. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, ButtonBase, Skeleton, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export const FACT_BAND_CELL_MIN_PX = 132;

/** One cell of a fact band. */
export interface FactBandCell {
  id: string;
  title: string;
  datum: string;
  detail: string | null;
  /** An instant behind the datum: the tooltip carries the datum and this (the line's own
   *  title would show beside it). */
  tooltip?: string;
  /** The detail is an info note (something the app cannot act on): an info icon and words,
   *  never red. */
  note?: boolean;
}

/** Where a cell leads: a link (`to`), a button (`onClick`), or nowhere (null — plain text). */
export type FactBandTarget = { to: string } | { onClick: () => void } | null;

/** One line, cut with an ellipsis at the cell's edge; the full text is the title. */
const LINE_SX = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
} as const;

/** The band's chrome: page background, the bottom hairline, bleeding over the page's side
 *  padding. */
const BAND_SX = {
  mx: -3,
  px: 3,
  pt: 1.25,
  pb: 1.25,
  mb: 2,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'background.default',
} as const;

/** One column per cell, scrolling sideways below the cells' minimum width. */
const gridSx = (cells: number) =>
  ({
    listStyle: 'none',
    // Room for the focus ring, which the sideways scroll would clip.
    m: -0.75,
    p: 0.75,
    display: 'grid',
    gridTemplateColumns: `repeat(${cells}, minmax(${FACT_BAND_CELL_MIN_PX}px, 1fr))`,
    overflowX: 'auto',
  }) as const;

const TITLE_SX = { display: 'block', fontWeight: 600, whiteSpace: 'nowrap' } as const;
/** The detail line keeps its height when empty. */
const DETAIL_SX = { ...LINE_SX, minHeight: 20 } as const;

const CELL_SX = {
  width: '100%',
  minWidth: 0,
  height: '100%',
  display: 'block',
  textAlign: 'left',
  pr: 1.5,
  borderRadius: 1,
  color: 'inherit',
  textDecoration: 'none',
  '&:hover .band-title': {
    color: 'primary.main',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
} as const;

export interface FactBandProps<C extends FactBandCell> {
  /** The region's name ("Account summary"). */
  label: string;
  /** The band's hook: drawn as `data-<name>-band` ("account" → `data-account-band`). */
  name: string;
  cells: readonly C[];
  /** Where each cell leads; absent → every cell is plain text. */
  targetOf?: (cell: C) => FactBandTarget;
}

export function FactBand<C extends FactBandCell>({
  label,
  name,
  cells,
  targetOf,
}: FactBandProps<C>) {
  const hook = { [`data-${name}-band`]: true };
  return (
    <Box component="section" aria-label={label} {...hook} sx={BAND_SX}>
      <Box component="ul" sx={gridSx(cells.length)}>
        {cells.map((c) => (
          <Box component="li" key={c.id} sx={{ minWidth: 0 }}>
            <Cell cell={c} target={targetOf ? targetOf(c) : null} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Cell({ cell, target }: { cell: FactBandCell; target: FactBandTarget }) {
  const body = <CellBody cell={cell} />;
  if (target && 'to' in target) {
    return (
      <ButtonBase component={RouterLink} to={target.to} sx={CELL_SX} data-band-cell={cell.id}>
        {body}
      </ButtonBase>
    );
  }
  if (target) {
    return (
      <ButtonBase onClick={target.onClick} sx={CELL_SX} data-band-cell={cell.id}>
        {body}
      </ButtonBase>
    );
  }
  return (
    <Box sx={{ pr: 1.5 }} data-band-cell={cell.id}>
      {body}
    </Box>
  );
}

function CellBody({ cell }: { cell: FactBandCell }) {
  return (
    <>
      <Typography className="band-title" variant="body2" component="span" sx={TITLE_SX}>
        {cell.title}
      </Typography>
      {cell.tooltip ? (
        // The instant's tooltip carries the datum too (a title of its own would show beside
        // it); describeChild keeps the datum's text as its name and titles it while closed.
        <Tooltip title={`${cell.datum} · ${cell.tooltip}`} placement="bottom-start" describeChild>
          <Typography variant="caption" component="span" sx={{ ...LINE_SX, color: 'text.primary' }}>
            {cell.datum}
          </Typography>
        </Tooltip>
      ) : (
        <BandLine text={cell.datum} sx={{ ...LINE_SX, color: 'text.primary' }}>
          {cell.datum}
        </BandLine>
      )}
      <BandLine
        text={cell.detail ?? ''}
        sx={{ ...DETAIL_SX, color: 'text.secondary' }}
        {...(cell.note ? { note: true } : {})}
      >
        {cell.note && (
          <InfoOutlinedIcon
            aria-hidden
            sx={{ fontSize: 13, mr: 0.5, verticalAlign: '-2px', color: 'info.main' }}
          />
        )}
        {cell.detail ?? ''}
      </BandLine>
    </>
  );
}

/** A datum or detail line. Cut by its ellipsis ("4.6★ · 2,054 review…"), it shows its full
 *  text in a tooltip — measured as the tooltip is about to open, so a line that fits shows
 *  none. The tooltip owns the line's title (`describeChild` gives the line its full text as
 *  its title while closed): a `title` of the line's own would be a second one, and MUI says
 *  so on every render. */
function BandLine({
  text,
  sx,
  note,
  children,
}: {
  text: string;
  sx: SxProps<Theme>;
  note?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const line = (
    <Typography
      ref={ref}
      variant="caption"
      component="span"
      data-band-line
      {...(note ? { 'data-band-note': true } : {})}
      sx={sx}
    >
      {children}
    </Typography>
  );
  // An empty line (a detail the cell has none of) keeps its height, with nothing to say.
  if (!text) return line;
  return (
    <Tooltip
      title={text}
      placement="bottom-start"
      describeChild
      open={open}
      onOpen={() => {
        const el = ref.current;
        setOpen(el !== null && el.scrollWidth > el.clientWidth);
      }}
      onClose={() => setOpen(false)}
    >
      {line}
    </Tooltip>
  );
}

/** The band while its record loads (`cells` columns): the band's chrome and grid, and per cell
 *  its title, datum and detail lines on their own boxes — so nothing moves when it loads. */
export function FactBandSkeleton({ cells }: { cells: number }) {
  return (
    <Box sx={BAND_SX} data-fact-band-skeleton>
      <Box sx={gridSx(cells)}>
        {Array.from({ length: cells }, (_, i) => (
          <Box key={i} sx={{ minWidth: 0 }}>
            <Box sx={{ pr: 1.5 }} data-fact-band-skeleton-cell>
              <Typography variant="body2" component="span" sx={TITLE_SX}>
                <Skeleton width="55%" />
              </Typography>
              <Typography variant="caption" component="span" sx={LINE_SX}>
                <Skeleton width="80%" />
              </Typography>
              <Typography variant="caption" component="span" sx={DETAIL_SX}>
                <Skeleton width="65%" />
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
